import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { DateString } from '@pezi/entities';
import moment from 'moment';

const dateNames: string[] = [];
for (let date = 1; date <= 31; date++) {
  dateNames.push(String(date));
}

@Injectable()
export class CustomDateAdapter extends DateAdapter<DateString> {

  ISO_FORMAT = 'YYYY-MM-DD';
  private localeData = moment.localeData('de-AT');
  private datePipe = new DatePipe('de-AT');

  format(date: DateString, displayFormat: Object): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd');
  }

  parse(value: any): any | null {
    const date = moment(value, this.ISO_FORMAT);
    return date.isValid() ? this.datePipe.transform(date.toDate(), 'yyyy-MM-dd') : null;
  }

  getFirstDayOfWeek(): number {
    return 1;
  }

  addCalendarDays(date: DateString, days: number): DateString {
    return moment(date)
      .add(days, 'day')
      .format(this.ISO_FORMAT);
  }

  addCalendarMonths(date: DateString, months: number): DateString {
    return moment(date)
      .add(months, 'month')
      .format(this.ISO_FORMAT);
  }

  addCalendarYears(date: DateString, years: number): DateString {
    return moment(date)
      .add(years, 'year')
      .format(this.ISO_FORMAT);
  }

  clone(date: DateString): DateString {
    return date;
  }

  createDate(year: number, month: number, date: number): DateString {
    return moment([
      year,
      month,
      date
    ])
      .format(this.ISO_FORMAT);
  }

  getDate(date: DateString): number {
    return moment(date)
      .date();
  }

  getDateNames(): string[] {
    return dateNames;
  }

  getDayOfWeek(date: DateString): number {
    return moment(date)
      .isoWeekday();
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    switch (style) {
      case 'long':
        return this.localeData.weekdays();
      case 'short':
        return this.localeData.weekdaysShort();
      case 'narrow':
        // Moment does not accept format even though @types/moment suggests it does
        return this.localeData.weekdaysShort();
    }
  }

  getMonth(date: DateString): number {
    return moment(date)
      .month();
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    switch (style) {
      case 'long':
        return this.localeData.months();
      case 'short':
        return this.localeData.monthsShort();
      case 'narrow':
        return this.localeData.monthsShort()
          .map(month => month[0]);
    }
  }

  getNumDaysInMonth(date: DateString): number {
    return moment(date)
      .daysInMonth();
  }

  getYear(date: DateString): number {
    return moment(date)
      .year();
  }

  getYearName(date: DateString): string {
    return String(moment(date)
      .year());
  }

  invalid(): DateString {
    return '';
  }

  isDateInstance(obj: any): boolean {
    return typeof obj === 'string';
  }

  isValid(date: DateString): boolean {
    return moment(date)
      .isValid();
  }

  toIso8601(date: DateString): string {
    return moment(date)
      .format(this.ISO_FORMAT);
  }

  today(): DateString {
    return moment()
      .format(this.ISO_FORMAT);
  }

}
